/**
 * garage-fit.js — Factory 47 unified-garage collection fit badges (WS2)
 *
 * Renders a per-card "Fits your <bike>" / "Doesn't fit your <bike>" badge on
 * collection/search product cards, driven by the vehicle the shopper already
 * selected in the EasySearch YMM garage. It NEVER hides products (badge-only).
 *
 * Design contract (see task brief):
 *  - Single source of truth for the vehicle = localStorage["easysearch-garage-items"]
 *    (EasySearch YMM's own key). We do NOT create a parallel garage.
 *  - Fitment truth = product esr-tags. The card exposes them via data-fit-tags
 *    (slug form, e.g. "2017-2020-road-glide-fltrx") which we match against the
 *    slug(s) the selected vehicle resolves to, using the baked map asset.
 *  - Bucket A (has esr-tags): show Fits / Doesn't fit.
 *  - Bucket B (data-fit-universal="true", i.e. tag nm-es-universal): always Fits.
 *  - Bucket C (no fitment tags): the card carries NO data-fit-* attrs → never badged.
 *  - "Doesn't fit" is only ever shown on a product that IS tagged for other bikes
 *    but not the selected one. A merely-untagged product is never labelled.
 *  - Any failure (no garage, no map, unknown vehicle, parse error) => no badges.
 *  - Feature-flagged OFF by default; this file is only loaded when the flag is on.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'easysearch-garage-items';
  var DELIM = 'easysearch-preselect-delimiter';
  var BADGE_CLASS = 'f47-fit-badge';
  var CARD_SELECTOR = 'product-card[data-fit-tags], product-card[data-fit-universal]';

  var scriptEl = document.getElementById('garage-fit-script') || document.currentScript;
  var MAP_URL = scriptEl ? scriptEl.getAttribute('data-fitment-map') : null;

  var mapPromise = null; // memoised parsed map

  function safe(fn, fallback) {
    try { return fn(); } catch (e) { return fallback; }
  }

  /**
   * Read the currently active garage vehicle from EasySearch's localStorage.
   * Returns { year:Number, modelKey:String, label:String } or null.
   * Uses the first garage entry as the active vehicle (My-Garage multi-vehicle
   * selection index is app-internal; first entry is the pragmatic v1 choice).
   */
  function readActiveVehicle() {
    return safe(function () {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      var list = obj && obj.value;
      if (!Array.isArray(list) || !list.length) return null;
      var parts = String(list[0]).split(DELIM);
      var year = parseInt((parts[0] || '').trim(), 10);
      var modelRaw = (parts[1] || '').trim();
      if (!year || !modelRaw) return null;
      return {
        year: year,
        modelKey: modelRaw.replace(/\s+/g, ' ').toLowerCase(),
        label: year + ' ' + modelRaw
      };
    }, null);
  }

  function loadMap() {
    if (mapPromise) return mapPromise;
    if (!MAP_URL || !window.fetch) { mapPromise = Promise.resolve(null); return mapPromise; }
    mapPromise = fetch(MAP_URL, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return mapPromise;
  }

  /**
   * Resolve the active vehicle to the set of collection slugs it maps to.
   * Returns a Set of slugs, or null if the vehicle/model is unknown (=> no badges).
   */
  function resolveSlugs(vehicle, map) {
    if (!map || !map.models) return null;
    var entries = map.models[vehicle.modelKey];
    if (!entries || !entries.length) return null; // unknown vehicle -> can't determine
    var slugs = new Set();
    for (var i = 0; i < entries.length; i++) {
      var lo = entries[i][0], hi = entries[i][1], slug = entries[i][2];
      if (vehicle.year >= lo && vehicle.year <= hi) slugs.add(slug);
    }
    return slugs; // may be empty (model known but not in a year that matches)
  }

  function clearBadges(root) {
    var scope = root || document;
    var badges = scope.querySelectorAll('.' + BADGE_CLASS);
    for (var i = 0; i < badges.length; i++) badges[i].parentNode && badges[i].parentNode.removeChild(badges[i]);
  }

  function buildBadge(status, label) {
    var span = document.createElement('span');
    span.className = BADGE_CLASS + ' ' + BADGE_CLASS + '--' + status;
    span.setAttribute('role', 'status');
    var text = status === 'fit' ? 'Fits your ' + label : "Doesn't fit your " + label;
    span.setAttribute('aria-label', text);
    var icon = document.createElement('span');
    icon.className = BADGE_CLASS + '__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = status === 'fit' ? '✓' : '✕'; // ✓ / ✕
    var txt = document.createElement('span');
    txt.className = BADGE_CLASS + '__text';
    txt.textContent = status === 'fit' ? 'Fits your bike' : "Doesn't fit";
    span.appendChild(icon);
    span.appendChild(txt);
    return span;
  }

  function badgeCard(card, slugs, label) {
    // idempotent: clear any existing badge on this card first
    clearBadges(card);
    var universal = card.getAttribute('data-fit-universal') === 'true';
    var status;
    if (universal) {
      status = 'fit';
    } else {
      var tags = (card.getAttribute('data-fit-tags') || '').split(/\s+/).filter(Boolean);
      if (!tags.length) return; // Bucket C safety net
      var fits = tags.some(function (s) { return slugs.has(s); });
      status = fits ? 'fit' : 'nofit';
    }
    var badge = buildBadge(status, label);
    // Render directly under the product title (top-left of the media is reserved
    // for the theme's sold-out / discount labels).
    var titleEl = card.querySelector('.card__title');
    if (titleEl) {
      titleEl.insertAdjacentElement('afterend', badge);
    } else {
      (card.querySelector('.card__info-inner') || card).appendChild(badge);
    }
  }

  function run() {
    var vehicle = readActiveVehicle();
    if (!vehicle) { clearBadges(); return; }
    loadMap().then(function (map) {
      var slugs = resolveSlugs(vehicle, map);
      if (!slugs) { clearBadges(); return; } // unknown vehicle / no map => graceful no-op
      var cards = document.querySelectorAll(CARD_SELECTOR);
      for (var i = 0; i < cards.length; i++) safe(function () { badgeCard(cards[i], slugs, vehicle.label); });
    });
  }

  // Re-badge cards added later (AJAX pagination, filtering) — debounced.
  var pending = null;
  function scheduleRun() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(run, 120);
  }

  function init() {
    if (!document.querySelector(CARD_SELECTOR)) {
      // No fitment-bearing cards on this page; still wire storage listener cheaply.
    }
    run();
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) run();
    });
    if (window.MutationObserver) {
      var obs = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          for (var j = 0; j < muts[i].addedNodes.length; j++) {
            var n = muts[i].addedNodes[j];
            if (n.nodeType === 1 && (n.matches && n.matches(CARD_SELECTOR) || n.querySelector && n.querySelector(CARD_SELECTOR))) {
              scheduleRun();
              return;
            }
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
